import type { ChangeEvent, MouseEvent } from 'react';
import { useCallback, useMemo, useState } from 'react';

import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  TableSortLabel,
} from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { useDebounce } from 'react-use';
import { useRecoilValue } from 'recoil';

import { Button, CenteredText, Icon } from '../../../components/base';
import useConfirmLeavePage from '../../../hooks/useConfirmLeavePage';
import { SearchInput } from '../../../shared/ui';
import { Drawer, useShowDrawer } from '../../../state/drawer';
import { userEventState } from '../../../state/event';
import { userState } from '../../../state/user';
import checkLimit from '../../../utils/checkLimit';
import { getComparator, type Order, type OrderBy } from '../lib/sortTable';
import { usersState } from '../model/atoms';
import { UsersTableRow } from './UsersTableRow';

const iconStyles = { width: '17px', height: '17px', marginRight: '10px' };

export const UsersTable = () => {
  const { t } = useTranslation();
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<OrderBy>('id');
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const users = useRecoilValue(usersState);
  const currentUser = useRecoilValue(userState);
  const { execute: showDrawer } = useShowDrawer();
  const userEvent = useRecoilValue(userEventState);
  const [changedUserEvents, setChangedUserEvents] = useState<Record<string, boolean>>({});
  const canAddNewUser = currentUser.isOwner || checkLimit(userEvent?.event.limitUsers, users.length);
  const handleChange = useCallback(
    (id: number | undefined, hasChanges: boolean) => {
      if (id) {
        setChangedUserEvents((oldValue) => ({
          ...oldValue,
          [id.toString()]: hasChanges,
        }));
      }
    },
    [setChangedUserEvents],
  );
  const usersHasChanges = Object.values(changedUserEvents).some(Boolean);
  const { confirmModal } = useConfirmLeavePage(usersHasChanges, t('you-have-unsaved-changes-confirm-leave'));

  const handleRequestSort = (event: MouseEvent<unknown>, property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleResetSearch = () => {
    setSearch('');
  };

  useDebounce(
    () => {
      setDebouncedSearch(search);
    },
    500,
    [search],
  );

  const rows = useMemo(
    () =>
      [...users]
        .sort(getComparator(order, orderBy))
        .filter((user) => `${user.firstName} ${user.lastName}`.toLowerCase().includes(debouncedSearch.toLowerCase())),
    [debouncedSearch, order, orderBy, users],
  );

  return (
    <>
      <Box display='flex' alignItems='center' pl={2} pt={2} pr={2}>
        {canAddNewUser ? (
          <Button
            type='submit'
            sx={{ marginTop: 'auto', color: 'white' }}
            variant='contained'
            onClick={() => showDrawer({ type: Drawer.USER_CREATE })}
          >
            <Icon icon='plus' sx={iconStyles} />
            <Trans i18nKey='add-new-user' />
          </Button>
        ) : (
          <Trans i18nKey='limit-users-for-event-reached-out' />
        )}
        <Box marginLeft='auto'>
          <SearchInput value={search} onChange={handleSearch} placeholder={t('search')} onReset={handleResetSearch} />
        </Box>
      </Box>
      {users.length > 0 ? (
        <Box p={2}>
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sortDirection={orderBy === 'id' ? order : false}>
                      <TableSortLabel
                        onClick={(event) => handleRequestSort(event, 'id')}
                        direction={orderBy === 'id' ? order : 'asc'}
                      >
                        <Trans i18nKey='id' />
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={orderBy === 'firstName' ? order : false}>
                      <TableSortLabel
                        onClick={(event) => handleRequestSort(event, 'firstName')}
                        direction={orderBy === 'firstName' ? order : 'asc'}
                      >
                        <Trans i18nKey='name' />
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={orderBy === 'email' ? order : false}>
                      <TableSortLabel
                        onClick={(event) => handleRequestSort(event, 'email')}
                        direction={orderBy === 'email' ? order : 'asc'}
                      >
                        <Trans i18nKey='email' />
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <Trans i18nKey='role' />
                    </TableCell>
                    <TableCell>
                      <Trans i18nKey='user-function' />
                    </TableCell>
                    <TableCell />
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((user) => (
                    <UsersTableRow onChange={handleChange} user={user} key={user.id} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      ) : (
        <CenteredText sx={{ marginTop: '50px' }}>
          <Trans i18nKey='users-list-is-empty' />
        </CenteredText>
      )}
      {confirmModal}
    </>
  );
};
